import { useState, useEffect, useRef } from 'react';

const useCaseAnimations = (categoryCases, categoryId) => {
  const [fadingOutCases, setFadingOutCases] = useState(new Set());
  const [collapsingCases, setCollapsingCases] = useState(new Set());
  const [successCollapsedCases, setSuccessCollapsedCases] = useState(new Set());
  const [dismissingCases, setDismissingCases] = useState(new Set());
  const [directDismissingCases, setDirectDismissingCases] = useState(new Set());
  const [displayedCases, setDisplayedCases] = useState(categoryCases || []);
  const [autoDismissProgress, setAutoDismissProgress] = useState(new Map());
  const autoDismissTimers = useRef(new Map());
  const progressIntervals = useRef(new Map());
  
  // Reset all animation state when category changes
  useEffect(() => {
    setFadingOutCases(new Set());
    setCollapsingCases(new Set());
    setSuccessCollapsedCases(new Set());
    setDismissingCases(new Set());
    setDirectDismissingCases(new Set());
    setDisplayedCases(categoryCases || []);
    setAutoDismissProgress(new Map());
    
    // Clear all auto-dismiss timers and progress intervals
    autoDismissTimers.current.forEach(timer => clearTimeout(timer));
    autoDismissTimers.current.clear();
    progressIntervals.current.forEach(interval => clearInterval(interval));
    progressIntervals.current.clear();
  }, [categoryId]);

  // Detect when a case should transition to success state (success=true, submitting=false)
  useEffect(() => {
    if (!categoryCases) return;

    categoryCases.forEach((caseData) => {
      const shouldCollapse = caseData.success && !caseData.submitting;
      const isFadingOut = fadingOutCases.has(caseData._id);
      const isSuccessCollapsed = successCollapsedCases.has(caseData._id);
      const isDirectDismissing = directDismissingCases.has(caseData._id);

      // Only show success state if not being directly dismissed (error cases)
      if (shouldCollapse && !isFadingOut && !isSuccessCollapsed && !isDirectDismissing) {
        // Start fade out - collapse will be triggered by onTransitionEnd
        setFadingOutCases(prev => new Set(prev).add(caseData._id));
      }
    });
  }, [categoryCases, fadingOutCases, successCollapsedCases, directDismissingCases]);

  // Update displayed cases when categoryCases changes (new cases added or updated)
  useEffect(() => {
    if (!categoryCases) return;
    
    setDisplayedCases(prev => {
      const prevIds = new Set(prev.map(c => c.caseId || c._id));
      const currentIds = new Set(categoryCases.map(c => c.caseId || c._id));
      
      // Find truly new cases (not in prev and not currently being dismissed)
      const newCases = categoryCases.filter(c => {
        const caseId = c.caseId || c._id;
        const isBeingDismissed = dismissingCases.has(caseId) || directDismissingCases.has(caseId);
        return !prevIds.has(caseId) && !isBeingDismissed;
      });
      
      // Keep cases that should remain visible
      const casesToKeep = prev.filter(prevCase => {
        const prevCaseId = prevCase.caseId || prevCase._id;
        const isBeingDismissed = dismissingCases.has(prevCaseId) || directDismissingCases.has(prevCaseId);
        
        // If case is being dismissed, keep it only if it's still animating
        if (isBeingDismissed) {
          return fadingOutCases.has(prevCaseId) || 
                 collapsingCases.has(prevCaseId) ||
                 successCollapsedCases.has(prevCaseId);
        }
        
        // Otherwise keep if still in Redux OR actively animating to success state
        return currentIds.has(prevCaseId) || 
               fadingOutCases.has(prevCaseId) || 
               collapsingCases.has(prevCaseId) || 
               successCollapsedCases.has(prevCaseId);
      });
      
      // Update existing cases with fresh data (but don't update dismissing cases)
      const updatedCases = casesToKeep.map(prevCase => {
        const prevCaseId = prevCase.caseId || prevCase._id;
        const isBeingDismissed = dismissingCases.has(prevCaseId) || directDismissingCases.has(prevCaseId);
        
        // Don't update cases that are being dismissed - keep their current state
        if (isBeingDismissed) {
          return prevCase;
        }
        
        const updated = categoryCases.find(c => (c.caseId || c._id) === prevCaseId);
        return updated || prevCase;
      });
      
      // Combine updated cases with new cases
      const result = [...updatedCases, ...newCases];
      
      // Only return new reference if something actually changed
      if (result.length === prev.length && 
          result.every((c, i) => c === prev[i])) {
        return prev;
      }
      
      return result;
    });
  }, [categoryCases, fadingOutCases, collapsingCases, successCollapsedCases, dismissingCases, directDismissingCases]);

  // Callback when fade transition completes
  const onFadeComplete = (caseId) => {
    const isDismissing = dismissingCases.has(caseId) || directDismissingCases.has(caseId);
    
    if (fadingOutCases.has(caseId)) {
      if (isDismissing) {
        // For dismissing cases, start collapse animation
        setCollapsingCases(prev => new Set(prev).add(caseId));
      } else {
        // For success transitions, go directly to collapsed success state
        setFadingOutCases(prev => {
          const next = new Set(prev);
          next.delete(caseId);
          return next;
        });
        setSuccessCollapsedCases(prev => new Set(prev).add(caseId));
        
        // Start progress tracking (update every 100ms for smooth animation)
        const startTime = Date.now();
        const duration = 10000; // 10 seconds
        
        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min((elapsed / duration) * 100, 100);
          
          setAutoDismissProgress(prev => {
            const next = new Map(prev);
            next.set(caseId, progress);
            return next;
          });
          
          if (progress >= 100) {
            clearInterval(progressInterval);
            progressIntervals.current.delete(caseId);
          }
        }, 100);
        
        progressIntervals.current.set(caseId, progressInterval);
        
        // Start 10 second auto-dismiss timer
        const timerId = setTimeout(() => {
          dismissSuccessCase(caseId, false);
          autoDismissTimers.current.delete(caseId);
          progressIntervals.current.delete(caseId);
        }, duration);
        autoDismissTimers.current.set(caseId, timerId);
      }
    }
  };

  // Helper to remove case from all animation state sets
  const clearCaseFromAllStates = (caseId) => {
    setFadingOutCases(prev => {
      const next = new Set(prev);
      next.delete(caseId);
      return next;
    });
    setCollapsingCases(prev => {
      const next = new Set(prev);
      next.delete(caseId);
      return next;
    });
    setSuccessCollapsedCases(prev => {
      const next = new Set(prev);
      next.delete(caseId);
      return next;
    });
    setDismissingCases(prev => {
      const next = new Set(prev);
      next.delete(caseId);
      return next;
    });
    setDirectDismissingCases(prev => {
      const next = new Set(prev);
      next.delete(caseId);
      return next;
    });
  };

  // Callback when collapse transition completes
  const onCollapseComplete = (caseId) => {
    const isDismissing = dismissingCases.has(caseId);
    const isDirectDismissing = directDismissingCases.has(caseId);
    
    if (isDismissing || isDirectDismissing) {
      // Remove from DOM and clear all animation states
      setDisplayedCases(prev => prev.filter(c => (c.caseId || c._id) !== caseId));
      clearCaseFromAllStates(caseId);
    }
  };

  const getCaseAnimationState = (caseId) => {
    const isDismissing = dismissingCases.has(caseId);
    const isDirectDismissing = directDismissingCases.has(caseId);
    const isSuccessCollapsed = successCollapsedCases.has(caseId);
    const isBeingDismissed = isDismissing || isDirectDismissing;
    
    return {
      // Fade out when explicitly fading OR when being dismissed
      isFadingOut: fadingOutCases.has(caseId) || isBeingDismissed,
      // Only collapse when explicitly collapsing OR being dismissed (not during initial success transition)
      isCollapsing: collapsingCases.has(caseId) || isBeingDismissed,
      // Keep success collapsed state during dismissal to prevent flash of expanded card
      isSuccessCollapsed: isSuccessCollapsed,
      // Progress percentage for auto-dismiss timer (0-100)
      autoDismissProgress: autoDismissProgress.get(caseId) || 0,
    };
  };

  const dismissSuccessCase = (caseId, isDirect = false) => {
    if (dismissingCases.has(caseId) || directDismissingCases.has(caseId)) return;
    
    // Clear any pending auto-dismiss timer and progress interval
    const timer = autoDismissTimers.current.get(caseId);
    if (timer) {
      clearTimeout(timer);
      autoDismissTimers.current.delete(caseId);
    }
    
    const interval = progressIntervals.current.get(caseId);
    if (interval) {
      clearInterval(interval);
      progressIntervals.current.delete(caseId);
    }
    
    setAutoDismissProgress(prev => {
      const next = new Map(prev);
      next.delete(caseId);
      return next;
    });
    
    if (isDirect) {
      // Direct dismissal (error cases) - fade out and collapse simultaneously
      setDirectDismissingCases(prev => new Set(prev).add(caseId));
      setFadingOutCases(prev => new Set(prev).add(caseId));
      setCollapsingCases(prev => new Set(prev).add(caseId));
    } else {
      // Success dismissal - fade out and collapse simultaneously
      setDismissingCases(prev => new Set(prev).add(caseId));
      setFadingOutCases(prev => new Set(prev).add(caseId));
      setCollapsingCases(prev => new Set(prev).add(caseId));
    }
  };

  // Cleanup timers and intervals on unmount
  useEffect(() => {
    return () => {
      autoDismissTimers.current.forEach(timer => clearTimeout(timer));
      autoDismissTimers.current.clear();
      progressIntervals.current.forEach(interval => clearInterval(interval));
      progressIntervals.current.clear();
    };
  }, []);

  return {
    displayedCases,
    getCaseAnimationState,
    dismissSuccessCase,
    onFadeComplete,
    onCollapseComplete,
  };
};

export default useCaseAnimations;
